import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Inventory,
  AccountTree,
  Factory,
  Route,
  LocationOn,
  CheckCircle,
  People,
  Restaurant,
  School,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import {
  fetchItems,
  fetchBOMs,
  fetchWorkCenters,
  fetchRoutings,
  fetchLocations,
} from '../../store/slices/masterDataSlice';

import ItemsTable from './components/ItemsTable';
import BOMManager from './components/BOMManager';
import WorkCentersTable from './components/WorkCentersTable';
import RoutingsTable from './components/RoutingsTable';
import LocationsTree from './components/LocationsTree';
import PersonnelTab from './components/PersonnelTab';
import KitchensTab from './components/KitchensTab';
import SchoolsTab from './components/SchoolsTab';

const MasterData: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    items,
    boms,
    workCenters,
    routings,
    locations,
    itemsLoading,
    bomsLoading,
    workCentersLoading,
    routingsLoading,
    locationsLoading,
    error,
  } = useSelector((state: RootState) => state.masterData);

  const [selectedTab, setSelectedTab] = React.useState(0);

  useEffect(() => {
    dispatch(fetchItems());
    dispatch(fetchBOMs());
    dispatch(fetchWorkCenters());
    dispatch(fetchRoutings());
    dispatch(fetchLocations());
  }, [dispatch]);

  const isLoading = itemsLoading || bomsLoading || workCentersLoading || routingsLoading || locationsLoading;

  const activeItems = items.filter((i) => i.status === 'active').length;
  const activeWorkCenters = workCenters.filter((wc) => wc.status === 'active').length;
  const activeBOMs = boms.filter((b) => b.status === 'active').length;
  const activeRoutings = routings.filter((r) => r.status === 'active').length;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Master Data Management
        </Typography>
        {isLoading && <CircularProgress size={24} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="caption">
                    Items & Products
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h5" color="primary.main">
                      {items.length}
                    </Typography>
                    <Chip label={`${activeItems} active`} size="small" color="success" />
                  </Box>
                </Box>
                <Inventory sx={{ fontSize: 32, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="caption">
                    Bill of Materials
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h5" color="info.main">
                      {boms.length}
                    </Typography>
                    <Chip label={`${activeBOMs} active`} size="small" color="success" />
                  </Box>
                </Box>
                <AccountTree sx={{ fontSize: 32, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="caption">
                    Work Centers
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h5" color="warning.main">
                      {workCenters.length}
                    </Typography>
                    <Chip label={`${activeWorkCenters} active`} size="small" color="success" />
                  </Box>
                </Box>
                <Factory sx={{ fontSize: 32, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="caption">
                    Routings
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h5" color="success.main">
                      {routings.length}
                    </Typography>
                    <Chip label={`${activeRoutings} active`} size="small" color="success" />
                  </Box>
                </Box>
                <Route sx={{ fontSize: 32, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Inventory />} iconPosition="start" label="Items & Products" />
          <Tab icon={<AccountTree />} iconPosition="start" label="Bill of Materials" />
          <Tab icon={<Factory />} iconPosition="start" label="Work Centers" />
          <Tab icon={<Route />} iconPosition="start" label="Routings" />
          <Tab icon={<LocationOn />} iconPosition="start" label="Locations" />
          <Tab icon={<People />} iconPosition="start" label="Personnel" />
          <Tab icon={<Restaurant />} iconPosition="start" label="Kitchens" />
          <Tab icon={<School />} iconPosition="start" label="Schools" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Items & Products
          </Typography>
          <ItemsTable />
        </Box>
      )}

      {selectedTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Bill of Materials
          </Typography>
          <BOMManager />
        </Box>
      )}

      {selectedTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Work Centers
          </Typography>
          <WorkCentersTable />
        </Box>
      )}

      {selectedTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Routings
          </Typography>
          <RoutingsTable />
        </Box>
      )}

      {selectedTab === 4 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Locations
          </Typography>
          <LocationsTree />
        </Box>
      )}

      {selectedTab === 5 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Personnel
          </Typography>
          <PersonnelTab />
        </Box>
      )}

      {selectedTab === 6 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Kitchen Profiles
          </Typography>
          <KitchensTab />
        </Box>
      )}

      {selectedTab === 7 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            School Masters
          </Typography>
          <SchoolsTab />
        </Box>
      )}
    </Box>
  );
};

export default MasterData;
